import { createContext, type PropsWithChildren, useCallback, useContext, useMemo } from "react";

import { type LoadableTrack, type PlayerEngineActions, playerEngine } from "~/lib/playerEngine";

const playerActionsContext = createContext<PlayerEngineActions>(playerEngine);

function useInternalPlayerMutations() {
  const play = useCallback(async (fromPosition?: number) => {
    await playerEngine.play(fromPosition);
  }, []);

  const pause = useCallback(() => {
    playerEngine.pause();
  }, []);

  const setPosition = useCallback((pos: number) => {
    playerEngine.setPosition(pos);
  }, []);

  const setVolume = useCallback((volume: number) => {
    playerEngine.setVolume(volume);
  }, []);

  const loadSequence = useCallback((sources: Array<LoadableTrack>, startingProgress = 0) => {
    return playerEngine.loadSequence(sources, startingProgress);
  }, []);

  const getPosition = useCallback(() => {
    return playerEngine.getPosition();
  }, []);

  const actions = useMemo(() => {
    return {
      play,
      pause,
      setPosition,
      setVolume,
      loadSequence,
      getPosition,
    } satisfies PlayerEngineActions;
  }, [play, pause, setPosition, setVolume, loadSequence, getPosition]);

  return actions;
}
export function PlayerActionsContextProvider(props: PropsWithChildren) {
  const actions = useInternalPlayerMutations();
  return <playerActionsContext.Provider value={actions}>{props.children}</playerActionsContext.Provider>;
}
export function usePlayerActions() {
  return useContext(playerActionsContext);
}
